<template>
  <v-card>
    <v-toolbar flat color="white" density="compact">
      <v-text-field
          density="compact"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          color="primary"
          v-model="search"
          hide-details
          single-line
          flat
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('search') }}</span>
        </template>
      </v-text-field>
      <v-autocomplete
          v-if="show_platform_dropdown"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.cluster"
          :items="advanced_search_models.cluster"
          @update:model-value="showRecords"
          :return-object="false"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('cluster') }}</span>
        </template>
      </v-autocomplete>
      <v-autocomplete
          v-if="show_platform_dropdown"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.unit"
          :items="advanced_search_models.unit"
          @update:model-value="showRecords"
          :return-object="false"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('unit') }}</span>
        </template>
      </v-autocomplete>
      <v-autocomplete
          v-if="show_platform_dropdown"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.root_asset_name"
          :items="advanced_search_models.root_asset_name"
          @update:model-value="showRecords"
          :return-object="false"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('root_asset_name') }}</span>
        </template>
      </v-autocomplete>
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.equipment_type_name"
          :items="advanced_search_models.equipment_type_name"
          @update:model-value="showRecords"
          :return-object="false"
          item-value="value"
          item-title="text"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('equipment_type') }}</span>
        </template>
      </v-autocomplete>
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.brand_name"
          :items="advanced_search_models.brand_name"
          @update:model-value="showRecords"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('brand') }}</span>
        </template>
      </v-autocomplete>
      <v-spacer/>
      <div class="chip-container">
        <base-action-button v-if="show_swap_button && canSwapArticle" @action="$emit('replaceArticle')">
          <template #prepend><PhSwap style="vertical-align: middle; margin-right: 5px;" size="20" /></template>
          {{ $store.getters.translate("replace") }} {{ $store.getters.translate("article") }}
        </base-action-button>
        <base-action-button v-if="show_buttons && canShowEquipmentTypes" @action="$router.push('/equipmenttypes')">
          <template #prepend><PhGear style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("equipmenttypes") }}
        </base-action-button>
        <base-action-button v-if="show_buttons && canCreate" @action="$emit('create')">
          <template #prepend><PhPlus style="vertical-align: middle; margin-right: 5px;" size="20"/></template>
          {{ $store.getters.translate("create") }}
        </base-action-button>
      </div>
    </v-toolbar>
    <div style="overflow-x: auto; width: 100%;">
      <v-data-table
          :headers="headers"
          :items="getRecords"
          class="elevation-1"
          @click:row="handleClick"
          :search="search"
          v-bind="footer_props"
          v-model:items-per-page="options.itemsPerPage"
          v-model:page="options.page"
          :show-select="false">
        <template #item.equipment_type_name="{ item }">
          {{ $store.getters.translate(item.equipment_type_name) }}
        </template>
        <template #item.related_records="{ item }">
          <v-chip size="small" color="primary" variant="tonal">
            {{ item.amount_of_inspections + " I · " + item.amount_of_defects + " D · " + item.amount_of_measurements + " M" }}
          </v-chip>
        </template>
        <template #item.actions="{ item }">
          <v-menu>
            <template #activator="{ props }">
              <v-btn elevation="0" icon v-bind="props">
                <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canEdit" @click="$emit('edit', item)">
                <template #prepend><PhPencilSimpleLine size="24"/></template>
                <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDelete" @click="deleteRecord(item.id)">
                <template #prepend><PhTrash size="24"/></template>
                <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<script>
import {useAbility} from "@casl/vue";
import helpFunctions from "../../plugins/helpFunctions";
import BaseActionButton from "./BaseActionButton";
import {PhDotsThreeVertical, PhGear, PhPencilSimpleLine, PhPlus, PhSwap, PhTrash} from "@phosphor-icons/vue";

export default {
  components: { BaseActionButton, PhSwap, PhPlus, PhGear, PhTrash, PhPencilSimpleLine, PhDotsThreeVertical },
  props: ["show_buttons", "show_swap_button", "show_platform_dropdown"],
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.articles,
      headers: [],
      all_records: [],
      records: [],
      options: helpFunctions.table_options,
      footer_props: helpFunctions.footer_props,
      search: '',
      advanced_search: {
        cluster: null,
        unit: null,
        root_asset_name: null,
        equipment_type_name: null,
        brand_name: null,
      },
      advanced_search_models: {
        cluster: [],
        unit: [],
        root_asset_name: [],
        equipment_type_name: [],
        brand_name: [],
      },
    };
  },
  created() {
    this.headers = helpFunctions.composeTableHeaders(helpFunctions.headers.articles);
  },
  methods: {
    fillAdvancedSearchArrays() {
      let processed_equipment_types = [];
      this.all_records.forEach(record => {
        if(record.cluster && !this.advanced_search_models.cluster.includes(record.cluster)) {
          this.advanced_search_models.cluster.push(record.cluster);
        }
        if(record.unit && !this.advanced_search_models.unit.includes(record.unit)) {
          this.advanced_search_models.unit.push(record.unit);
        }
        if(record.root_asset_name && !this.advanced_search_models.root_asset_name.includes(record.root_asset_name)) {
          this.advanced_search_models.root_asset_name.push(record.root_asset_name);
        }
        if(record.equipment_type_name && !processed_equipment_types.includes(record.equipment_type_name)) {
          processed_equipment_types.push(record.equipment_type_name);
          this.advanced_search_models.equipment_type_name.push({ value: record.equipment_type_name, text: this.$store.getters.translate(record.equipment_type_name) });
        }
        if(record.brand_name && !this.advanced_search_models.brand_name.includes(record.brand_name)) {
          this.advanced_search_models.brand_name.push(record.brand_name);
        }
      });
      this.showRecords();
    },
    showRecords() {
      let sorted_array = this.all_records;
      if(this.advanced_search.cluster) {
        sorted_array = sorted_array.filter(item => item.cluster === this.advanced_search.cluster);
      }
      if(this.advanced_search.unit) {
        sorted_array = sorted_array.filter(item => item.unit === this.advanced_search.unit);
      }
      if(this.advanced_search.root_asset_name) {
        sorted_array = sorted_array.filter(item => item.root_asset_name === this.advanced_search.root_asset_name);
      }
      if(this.advanced_search.equipment_type_name) {
        sorted_array = sorted_array.filter(item => item.equipment_type_name === this.advanced_search.equipment_type_name);
      }
      if(this.advanced_search.brand_name) {
        sorted_array = sorted_array.filter(item => item.brand_name === this.advanced_search.brand_name);
      }
      this.records = sorted_array;
    },
    handleClick(event, row) {
      this.$emit('edit', row.item);
    },
    async deleteRecord(id) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, id);
          this.$emit('refresh');
        }
      });
    },
  },
  computed: {
    getRecords() {
      if(this.records) {
        return this.records;
      }
      else {
        return [];
      }
    },
    canSwapArticle() {
      return this.can("show", this.$createEntity('asset', { id: 0 })) &&
          this.can("show", this.$createEntity('article', { id: 0 })) &&
          this.can("edit", this.$createEntity('article', { id: 0 })) &&
          this.can("show", this.$createEntity('tagnumber', { id: 0 }));
    },
    canCreate() {
      return this.can("create", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canDelete() {
      return this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canShowEquipmentTypes() {
      return this.can("show", this.$createEntity('equipmenttype', { id: 0 }));
    },
  },
  watch: {
    options: {
      handler() {
        this.showRecords();
      },
      deep: true,
    },
    advanced_search: {
      handler() {
        this.options.page = 1;
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.chip-container {
  display: flex;
  gap: 8px;
  margin-right: 16px;
}
</style>